//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Icons used within the application
//
//  Copyright:    © 2021 - 2024 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier            Issue# Description
//region Version 1.0.0.0
//    001   15.07.21 Sean Flook         WI39??? Initial Revision.
//    002   06.10.23 Sean Flook                 Use colour variables.
//    003   24.11.23 Sean Flook                 Moved Box to @mui/system.
//    004   05.01.24 Sean Flook                 Use CSS shortcuts.
//    005   14.03.24 Sean Flook        ESU24_GP Added AssignEsuIcon.
//    006   04.04.24 Sean Flook                 Added SupportAgentIcon.
//endregion Version 1.0.0.0
//region Version 1.0.7.0
//    007   13.06.25 Sean Flook       IMANN-1812 Moved controls from @mui/system to @mui/material.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

//region imports

import React from "react";
import { SvgIcon, Box } from "@mui/material";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import { adsWhite, adsLightGreyD } from "./ADSColours";

//endregion imports

/**
 * Method to get the indent icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The indent icon.
 */
export function IndentIcon(props) {
  return (
    <Box
      {...props}
      sx={{
        borderBottomColor: adsLightGreyD,
        borderBottomLeftRadius: "6px",
        borderBottomStyle: "solid",
        borderBottomWidth: "2px",
        borderLeftColor: adsLightGreyD,
        borderLeftStyle: "solid",
        borderLeftWidth: "2px",
        height: "12px",
        mb: "10px",
        width: "9px",
      }}
    />
  );
}

/**
 * Method to get the start to end icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The start to end icon.
 */
export function StartToEndIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M15,5l-1.41,1.41L18.17,11H2V13h16.17l-4.59,4.59L15,19l7-7L15,5z" />
    </SvgIcon>
  );
}

/**
 * Method to get the end to start icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The end to start icon.
 */
export function EndToStartIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M9,19l1.41-1.41L5.83,13H22V11H5.83l4.59-4.59L9,5l-7,7L9,19z" />
    </SvgIcon>
  );
}

/**
 * Method to get the PRoW icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The PRoW icon.
 */
export function PRoWIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M22,11.547C20.344,11.547 18.951,10.675 18.254,9.456L17.382,8.061C16.895,7.287 15.919,6.972 15.074,7.33L10.675,9.194L10.675,13.289L12.417,13.289L12.417,10.327L13.985,9.717L11.546,22L13.376,22L14.943,15.031L16.773,16.773L16.773,22L18.515,22L18.515,15.466L16.686,13.724L17.209,11.111C18.341,12.418 20.083,13.289 22,13.289L22,11.547ZM2,7.252C2,7.8 2.445,8.245 2.992,8.245L5.755,8.245L5.755,22L8.079,22L8.079,8.245L11.104,8.245C11.373,8.245 11.631,8.135 11.818,7.941L13.743,5.946C14.097,5.579 14.116,5.002 13.784,4.614L11.859,2.357C11.67,2.135 11.394,2.008 11.104,2.008L2.992,2.008C2.445,2.008 2,2.453 2,3L2,7.252ZM17.209,6.824C18.167,6.824 18.951,6.04 18.951,5.081C18.951,4.123 18.167,3.339 17.209,3.339C16.25,3.339 15.466,4.123 15.466,5.081C15.466,6.04 16.25,6.824 17.209,6.824ZM10.652,4L10.646,3.993L3.985,3.993L3.985,6.26L4,6.26L4,4L10.652,4Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the quiet route icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The quiet route icon.
 */
export function QuietRouteIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M21,22L21,20L16,20L16,22L21,22ZM8,22L8,20L3,20L3,22L8,22ZM14.5,22L14.5,20L9.5,20L9.5,22L14.5,22ZM3,16.5C2.485,16.5 2,16.988 2,17.5C2,18.011 2.485,18.5 3,18.5L21,18.5C21.515,18.5 22,18.011 22,17.5C22,16.988 21.515,16.5 21,16.5L13,16.5L13,12.5C13.7,12.744 14.666,13.382 15.181,13.086C16.042,12.591 15.83,10.95 16.329,10.093C16.813,9.263 18.361,8.638 18.361,7.609C18.361,6.58 16.813,5.955 16.329,5.125C15.831,4.268 16.042,2.628 15.181,2.132C14.346,1.652 13.035,2.64 12,2.64C10.965,2.64 9.654,1.652 8.819,2.132C7.957,2.627 8.17,4.268 7.67,5.124C7.187,5.955 5.639,6.58 5.639,7.609C5.639,8.638 7.187,9.263 7.67,10.093C8.168,10.95 7.957,12.59 8.819,13.086C9.333,13.382 10.3,12.744 11,12.5L11,16.5L3,16.5Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the obstruction icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The obstruction icon.
 */
export function ObstructionIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M0,0h24v24H0V0z" fill="none" />
      </g>
      <g>
        <g>
          <path d="M12,2C6.48,2,2,6.48,2,12s4.48,10,10,10s10-4.48,10-10S17.52,2,12,2z M17,13H7v-2h10V13z" />
        </g>
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the planning order icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The planning order icon.
 */
export function PlanningOrderIcon(props) {
  return (
    <SvgIcon {...props}>
      <g id="Layer1">
        <path d="M20.5,3L20.34,3.03L15,5.1L9,3L3.36,4.9C3.15,4.97 3,5.15 3,5.38L3,20.5C3,20.78 3.22,21 3.5,21L3.66,20.97L9,18.9L15,21L20.64,19.1C20.85,19.03 21,18.85 21,18.62L21,3.5C21,3.22 20.78,3 20.5,3ZM12.156,18L12.5,18.121L12.5,18L12.156,18ZM11.097,12.018L16.379,6.736L18.5,8.857L13.218,14.139L13.222,14.143L11.101,16.264L6.851,12.014L8.972,9.893L11.097,12.018Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the vehicles prohibited icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The vehicles prohibited icon.
 */
export function VehiclesProhibitedIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M4.539,7.568L0,3.029L1.414,1.615L21,21.201L19.586,22.615L15.971,19L6,19L6,20C6,20.55 5.55,21 5,21L4,21C3.45,21 3,20.55 3,20L3,12L4.539,7.568ZM7.607,5L17.5,5C18.16,5 18.72,5.42 18.92,6.01L21,12L21,18.393L18.346,15.74C18.741,15.47 19,15.016 19,14.5C19,13.67 18.33,13 17.5,13C16.984,13 16.53,13.259 16.26,13.654L13.607,11L19,11L17.5,6.5L9.107,6.5L7.607,5ZM6.49,13C5.665,13.005 5,13.673 5,14.5C5,15.33 5.67,16 6.5,16C7.33,16 8,15.33 8,14.5C8,13.673 7.335,13.005 6.51,13L6.49,13ZM7.971,11L5.743,8.772L5,11L7.971,11Z" />
    </SvgIcon>
  );
}

/**
 * Method to get the copy icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The copy icon.
 */
export function CopyIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
    </SvgIcon>
  );
}

/**
 * Method to get the move icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The move icon.
 */
export function MoveIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M18.586,20.015L15,20.015L15,22.015L22,22.015L22,15L20,15L20,18.601L16.732,15.333L15.318,16.747L18.586,20.015ZM5.429,20L9.015,20L9.015,22L2.015,22L2.015,14.985L4.015,14.985L4.015,18.586L7.283,15.318L8.697,16.732L5.429,20ZM11.98,7.96C14.199,7.96 16,9.761 16,11.98C16,14.199 14.199,16 11.98,16C9.761,16 7.96,14.199 7.96,11.98C7.96,9.761 9.761,7.96 11.98,7.96ZM18.586,4L15,4L15,2L22,2L22,9.015L20,9.015L20,5.414L16.732,8.682L15.318,7.268L18.586,4ZM5.429,4L9.015,4L9.015,2L2.015,2L2.015,9.015L4.015,9.015L4.015,5.414L7.283,8.682L8.697,7.268L5.429,4Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the street icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The street icon.
 */
export function StreetIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M21,21L3,21L7.5,3L16.5,3L21,21ZM13,14L11,14L11,18L13,18L13,14ZM13,9L11,9L11,12L13,12L13,9ZM13,5L11,5L11,7L13,7L13,5Z" />
    </SvgIcon>
  );
}

/**
 * Method to get the add street icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The add street icon.
 */
export function AddStreetIcon(props) {
  return (
    <SvgIcon {...props}>
      <g transform="translate(0,-488)" id="g12">
        <g id="g10"></g>
      </g>

      <g transform="translate(0,-488)" id="g20"></g>
      <g transform="translate(0,-488)" id="g22"></g>
      <g transform="translate(0,-488)" id="g24"></g>
      <g transform="translate(0,-488)" id="g26"></g>
      <g transform="translate(0,-488)" id="g28"></g>
      <g transform="translate(0,-488)" id="g30"></g>
      <g transform="translate(0,-488)" id="g32"></g>
      <g transform="translate(0,-488)" id="g34"></g>
      <g transform="translate(0,-488)" id="g36"></g>
      <g transform="translate(0,-488)" id="g38"></g>
      <g transform="translate(0,-488)" id="g40"></g>
      <g transform="translate(0,-488)" id="g42"></g>
      <g transform="translate(0,-488)" id="g44"></g>
      <g transform="translate(0,-488)" id="g46"></g>
      <g transform="translate(0,-488)" id="g48"></g>
      <g transform="translate(-1.946918,-1.2947033)" id="g899">
        <g id="g6" transform="matrix(0.04603152,0,0,0.0410053,0.21593396,2.7893575)">
          <g id="g4">
            <path
              d="M 376.075,14.012 C 375.226,6.045 368.503,0 360.49,0 H 151.51 c -8.013,0 -14.736,6.045 -15.585,14.012 L 84.694,494.665 c -0.988,9.264 6.285019,17.35384 15.585,17.335 l 299.89625,-0.60767 c 11.5955,-30.31633 -61.93224,-80.83229 -57.44962,-106.31753 2.2413,-12.74262 12.74327,-1.21888 13.3105,-17.47438 0.56724,-16.25551 48.92849,-32.65892 47.89108,-55.57442 C 399.77758,240.364 383.6762,85.326992 376.075,14.012 Z m -84.632,466.641 -1.998,-81.474 c -0.139,-5.67 -4.775,-10.193 -10.446,-10.193 h -45.998 c -5.671,0 -10.307,4.523 -10.446,10.193 l -1.998,81.474 H 117.712 L 165.603,31.347 h 65.973 l -0.704,28.71 c -0.144,5.869 4.575,10.705 10.446,10.705 h 29.366 c 5.871,0 10.59,-4.836 10.446,-10.705 l -0.704,-28.71 h 66.58067 l 19.99564,227.0778 8.1748,88.86765 -32.6767,60.43691 33.55946,74.74665 z"
              id="path2"
            />
          </g>
        </g>
        <path
          d="m 13.230187,8.279434 c -0.0064,-0.261 -0.219802,-0.4692 -0.480844,-0.4692 h -1.498652 c -0.261043,0 -0.474446,0.2082 -0.480844,0.4692 l -0.05226,2.13084 c -0.0067,0.27016 0.210505,0.49277 0.480799,0.49277 h 1.603186 c 0.270254,0 0.487477,-0.2226 0.480844,-0.49277 z"
          id="path8"
          style={{ strokeWidth: "0.04603152" }}
        />
        <g id="g18" transform="matrix(0.04603152,0,0,0.04603152,0.21588485,1.9541489)">
          <g id="g16">
            <path
              d="m 285.706,246.709 c -0.138,-5.67 -4.774,-10.193 -10.445,-10.193 H 236.74 c -5.671,0 -10.307,4.523 -10.446,10.193 l -1.541,62.866 c -0.144,5.869 4.575,10.705 10.446,10.705 h 41.604 c 5.871,0 10.59,-4.836 10.446,-10.705 z"
              id="path14"
            />
          </g>
        </g>
        <g id="g6-7" transform="matrix(0.01748252,0,0,0.01748252,10.580653,14.904943)">
          <g id="g4-5" transform="translate(207.99997,-4.7999993)">
            <path
              d="M 292.19999,1.5999998 C 151.59,1.5999998 35.199998,115.99 35.199998,256.6 c 0,140.61 116.390002,257 256.999992,257 140.61,0 255,-116.39 255,-257 0,-140.61 -114.39,-255.0000002 -255,-255.0000002 z m 135,285.0000002 h -105 v 107 c 0,16.54 -13.47,30 -30,30 -16.54,0 -30,-13.46 -30,-30 v -107 H 155.2 c -16.54,0 -30,-13.46 -30,-30 0,-16.54 13.46,-30 30,-30 h 106.99999 v -105 c 0,-16.54 13.46,-30 30,-30 16.53,0 30,13.46 30,30 v 105 h 105 c 16.53,0 30,13.46 30,30 0,16.54 -13.47,30 -30,30 z"
              id="path2-5"
            />
          </g>
        </g>
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the whole road icon.
 *
 * @param {object} param0 The whole road flag and part road colour.
 * @returns {JSX.Element} The whole road icon
 */
export function WholeRoadIcon({ wholeRoad, partRoadColour }) {
  if (wholeRoad) return <LinearScaleIcon />;
  else
    return (
      <SvgIcon>
        <path
          id="path4"
          d="M 17.21,11 H 14.29 C 13.9,10.12 13.03,9.5 12,9.5 10.97,9.5 10.1,10.12 9.71,11 H 6.79 C 6.4,10.12 5.53,9.5 4.5,9.5 3.12,9.5 2,10.62 2,12 c 0,1.38 1.12,2.5 2.5,2.5 1.03,0 1.9,-0.62 2.29,-1.5 h 2.92 c 0.39,0.88 1.26,1.5 2.29,1.5 1.03,0 1.9,-0.62 2.29,-1.5 h 2.92 c 0,-2 0,0 0,-2 z"
        />
        <ellipse
          ry="2.1779661"
          rx="2.3144915"
          cy="11.940678"
          cx="19.772121"
          id="path841"
          style={{
            opacity: 1,
            fill: "none",
            fillOpacity: 1,
            stroke: partRoadColour ? partRoadColour : adsWhite,
            strokeWidth: 1,
            strokeLineJoin: "round",
            strokeMiterLimit: 5.5,
            strokeDashArray: "none",
            strokeOpacity: 1,
          }}
        />
      </SvgIcon>
    );
}

/**
 * Method to get the special designation icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The special designation icon.
 */
export function SpecialDesignationIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M18,19L21.25,19C21.664,19 22,19.336 22,19.75L22,21.25C22,21.664 21.664,22 21.25,22L2.766,22C2.352,22 2.016,21.664 2.016,21.25L2.016,19.75C2.016,19.336 2.352,19 2.766,19L6,19L6.746,16L17.254,16L18,19ZM16.508,13L7.492,13L8.735,8L15.265,8L16.508,13ZM14.519,5L9.481,5L10.227,2L13.773,2L14.519,5Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the move BLPU icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The move BLPU icon.
 */
export function MoveBLPUIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M18.586,20.015L15,20.015L15,22.015L22,22.015L22,15L20,15L20,18.601L16.732,15.333L15.318,16.747L18.586,20.015ZM5.429,20L9.015,20L9.015,22L2.015,22L2.015,14.985L4.015,14.985L4.015,18.586L7.283,15.318L8.697,16.732L5.429,20ZM11.98,7.96C14.199,7.96 16,9.761 16,11.98C16,14.199 14.199,16 11.98,16C9.761,16 7.96,14.199 7.96,11.98C7.96,9.761 9.761,7.96 11.98,7.96ZM18.586,4L15,4L15,2L22,2L22,9.015L20,9.015L20,5.414L16.732,8.682L15.318,7.268L18.586,4ZM5.429,4L9.015,4L9.015,2L2.015,2L2.015,9.015L4.015,9.015L4.015,5.414L7.283,8.682L8.697,7.268L5.429,4Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the unassign ESU icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The unassign ESU icon.
 */
export function UnassignEsuIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M18.604,11.418C20.597,12.244 22,14.21 22,16.5C22,19.536 19.536,22 16.5,22C15.324,22 14.233,21.63 13.339,21L3,21L7.5,3L16.5,3L18.604,11.418ZM13.302,20.974L13.306,20.977L13.302,20.974ZM13.27,20.951L13.272,20.952L13.27,20.951ZM13.202,20.901L13.204,20.902L13.202,20.901ZM13.137,20.851L13.138,20.852L13.137,20.851ZM12.941,20.692L12.944,20.694L12.941,20.692ZM12.815,20.582L12.818,20.584L12.815,20.582ZM12.785,20.555L12.787,20.556L12.785,20.555ZM12.46,20.231L12.462,20.233L12.46,20.231ZM12.376,20.138L12.378,20.139L12.376,20.138ZM12.349,20.107L12.35,20.108L12.349,20.107ZM12.268,20.011L12.269,20.013L12.268,20.011ZM11.965,19.611L11.967,19.613L11.965,19.611ZM11.895,19.507L11.896,19.509L11.895,19.507ZM11.805,19.364L11.807,19.368L11.805,19.364ZM11.741,19.257L11.742,19.259L11.741,19.257ZM11.719,19.219L11.722,19.224L11.719,19.219ZM11.7,19.185L11.701,19.187L11.7,19.185ZM13,5L13,7L11,7L11,5L9,5L6,19L11.601,19C11.217,18.25 11,17.4 11,16.5C11,13.536 13.35,11.116 16.287,11.004L15,5L13,5ZM20,15L13,15L13,18L20,18L20,15ZM13,9L11,9L11,11.5L13,11.5L13,9ZM18.202,11.269L18.204,11.269L18.202,11.269ZM18.138,11.248L18.145,11.251L18.138,11.248ZM18.041,11.219L18.052,11.223L18.041,11.219ZM18.009,11.21L18.021,11.213L18.009,11.21ZM17.781,11.15L17.795,11.154L17.781,11.15ZM17.715,11.135L17.73,11.138L17.715,11.135ZM17.681,11.127L17.697,11.131L17.681,11.127ZM17.414,11.076L17.431,11.079L17.414,11.076ZM17.38,11.07L17.397,11.073L17.38,11.07ZM17.279,11.055L17.296,11.057L17.279,11.055ZM17.245,11.05L17.262,11.052L17.245,11.05ZM17.211,11.046L17.228,11.048L17.211,11.046ZM17.177,11.041L17.194,11.043L17.177,11.041ZM17.108,11.033L17.126,11.035L17.108,11.033ZM17.039,11.026L17.057,11.028L17.039,11.026ZM17.005,11.023L17.023,11.025L17.005,11.023ZM16.901,11.014L16.919,11.016L16.901,11.014ZM16.866,11.012L16.885,11.013L16.866,11.012ZM16.832,11.01L16.85,11.011L16.832,11.01ZM16.762,11.006L16.78,11.007L16.762,11.006ZM16.727,11.005L16.745,11.005L16.727,11.005ZM16.692,11.003L16.711,11.004L16.692,11.003ZM16.656,11.002L16.676,11.003L16.656,11.002ZM16.621,11.001L16.641,11.002L16.621,11.001ZM16.584,11.001L16.606,11.001L16.584,11.001Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the assign ESU icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The assign ESU icon.
 */
export function AssignEsuIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M13.339,21L3,21L7.5,3L16.5,3L18.604,11.418C20.597,12.244 22,14.21 22,16.5C22,19.536 19.536,22 16.5,22C15.324,22 14.233,21.63 13.339,21ZM13.203,20.902L13.202,20.901L13.203,20.902ZM13.168,20.875L13.172,20.878L13.168,20.875ZM13.071,20.799L13.07,20.798L13.071,20.799ZM13.037,20.772L13.041,20.775L13.037,20.772ZM12.941,20.692L12.944,20.694L12.941,20.692ZM12.91,20.665L12.913,20.668L12.91,20.665ZM12.815,20.582L12.818,20.584L12.815,20.582ZM12.786,20.555L12.787,20.556L12.786,20.555ZM12.693,20.468L12.696,20.471L12.693,20.468ZM12.575,20.351L12.577,20.354L12.575,20.351ZM12.461,20.232L12.462,20.233L12.461,20.232ZM17.476,15.876L19.352,14L14.029,14.029L14,19.352L15.876,17.476L18.2,19.8L19.8,18.2L17.476,15.876ZM11.966,19.612L11.967,19.613L11.966,19.612ZM11.895,19.507L11.896,19.509L11.895,19.507ZM11.873,19.473L11.874,19.474L11.873,19.473ZM11.719,19.219L11.722,19.224L11.719,19.219ZM11.638,19.072L11.641,19.078L11.638,19.072ZM11.62,19.037L11.621,19.039L11.62,19.037ZM13,5L13,7L11,7L11,5L9,5L6,19L11.601,19C11.217,18.25 11,17.4 11,16.5C11,13.536 13.35,11.116 16.287,11.004L15,5L13,5ZM13,9L11,9L11,11.5L13,11.5L13,9ZM18.202,11.269L18.204,11.269L18.202,11.269ZM18.138,11.248L18.145,11.251L18.138,11.248ZM18.106,11.238L18.115,11.241L18.106,11.238ZM18.074,11.229L18.084,11.232L18.074,11.229ZM18.041,11.219L18.052,11.223L18.041,11.219ZM18.009,11.21L18.021,11.213L18.009,11.21ZM17.912,11.183L17.925,11.187L17.912,11.183ZM17.879,11.175L17.893,11.178L17.879,11.175ZM17.846,11.166L17.86,11.17L17.846,11.166ZM17.813,11.158L17.828,11.162L17.813,11.158ZM17.781,11.15L17.795,11.154L17.781,11.15ZM17.748,11.142L17.763,11.146L17.748,11.142ZM17.681,11.127L17.697,11.131L17.681,11.127ZM17.615,11.113L17.631,11.117L17.615,11.113ZM17.582,11.107L17.598,11.11L17.582,11.107ZM17.515,11.094L17.531,11.097L17.515,11.094ZM17.448,11.082L17.464,11.084L17.448,11.082ZM17.414,11.076L17.431,11.079L17.414,11.076ZM17.38,11.07L17.397,11.073L17.38,11.07ZM17.347,11.065L17.363,11.068L17.347,11.065ZM17.279,11.055L17.296,11.057L17.279,11.055ZM17.245,11.05L17.262,11.052L17.245,11.05ZM17.211,11.046L17.228,11.048L17.211,11.046ZM17.142,11.037L17.16,11.039L17.142,11.037ZM17.108,11.033L17.126,11.035L17.108,11.033ZM17.039,11.026L17.057,11.028L17.039,11.026ZM17.005,11.023L17.023,11.025L17.005,11.023ZM16.97,11.02L16.988,11.021L16.97,11.02ZM16.936,11.017L16.954,11.018L16.936,11.017ZM16.866,11.012L16.885,11.013L16.866,11.012ZM16.832,11.01L16.85,11.011L16.832,11.01ZM16.797,11.008L16.815,11.009L16.797,11.008ZM16.762,11.006L16.78,11.007L16.762,11.006ZM16.727,11.005L16.745,11.005L16.727,11.005ZM16.656,11.002L16.676,11.003L16.656,11.002ZM16.621,11.001L16.641,11.002L16.621,11.001ZM16.547,11L16.57,11L16.547,11ZM16.452,11L16.441,11L16.452,11ZM16.5,11L16.535,11L16.5,11Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the equestrian icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The equestrian icon.
 */
export function EquestrianIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="m 21.119811,17.022231 c -0.0751,0.187748 -0.145504,0.370801 -0.22999,0.539773 -0.394268,0.736907 -1.173419,1.079546 -1.952569,1.225049 v -1.337698 h 0.567934 c 0.107951,-0.276926 0.328558,-0.483447 0.614872,-0.572627 0.08917,-0.04693 0.145503,-0.122036 0.164279,-0.220604 0.0094,0 0.0094,0 0.0094,0 0.0751,-0.61487 0.17836,-1.262598 0.305088,-1.919712 0.06102,-0.164279 0.0048,-0.314477 -0.173665,-0.3708 -0.384881,-0.04693 -0.755682,-0.122036 -1.126482,-0.220603 -0.797925,-0.03755 -1.600543,-0.215909 -2.290514,-0.633647 -0.614871,0.333251 -0.840167,0.980979 -1.173418,1.548913 v -0.0094 c -0.788538,1.229743 -1.426878,2.543972 -1.919713,3.905138 -0.03286,0.08448 -0.04693,0.173666 -0.04693,0.262846 0,0.14081 0.04224,0.272233 0.107951,0.384881 0.375493,0.384882 0.619564,0.858943 0.812005,1.351778 l -1.483201,0.01409 c -0.0094,-0.356719 -0.04224,-0.699358 -0.253459,-0.999753 -0.187746,-0.164279 -0.333251,-0.305089 -0.361413,-0.567934 0.04224,-0.333251 0.253459,-0.628952 0.394269,-0.929348 v 0 c 0.173665,-0.370801 0.276927,-0.76507 0.328558,-1.164032 0.05633,-0.342637 0.150198,-0.680583 0.244071,-1.018527 0.276926,-0.924655 0.413043,-1.882164 0.60079,-2.830286 -1.670949,0.26754 -3.365365,0.14081 -5.0128458,-0.197135 -0.1830532,0.971591 -0.4599798,1.910327 -0.8120052,2.830286 -0.1314234,0.478756 0.2393777,0.966898 0.4740609,1.351779 0.5209989,0.769763 1.1077071,1.483202 1.6099311,2.267045 0.305088,0.375494 0.558547,0.797925 0.755682,1.248517 L 9.7048093,20.98369 9.6578763,20.411061 C 9.6531021,20.326587 9.6531021,20.242099 9.578062,20.199855 9.3527666,20.129456 9.2401177,19.932317 9.1603255,19.725794 8.9866588,19.308058 8.7660567,18.904401 8.5032113,18.51952 8.0197625,17.853018 7.4377472,17.271003 6.7712451,16.787554 c 0,0 0,0 -0.00938,-0.0094 -0.173667,-0.112635 -0.2065225,-0.295682 -0.2065225,-0.483429 0.065708,-0.352027 0.1361153,-0.704052 0.1971331,-1.060771 0.056327,-0.689971 -0.2534576,-1.351778 -0.8213931,-1.755435 -0.3050888,0.464674 -0.694663,0.854249 -1.1264819,1.206275 -0.5209976,0.366107 -1.013833,0.774457 -1.4691195,1.220355 v 0 c -0.1126539,0.112642 -0.2206033,0.220603 -0.3191699,0.328558 -0.2393778,0.309782 -0.4036566,0.671196 -0.5397733,1.032608 -0.2722332,0.694664 -0.4693673,1.365859 -0.6054838,2.008894 -0.046937,0.211214 0.00469,0.422429 0.1079544,0.605483 v 0 c 0.2440718,0.31917 0.441205,0.671196 0.5820161,1.06077 l -1.4597334,0.01409 -0.00938,-0.657114 c 0.00938,-0.06102 -0.014081,-0.09388 -0.046937,-0.14081 h -0.00938 C 0.78213305,20.030893 0.68825964,19.805597 0.72580895,19.533364 1.1764018,18.444431 1.3453741,17.52447 1.6129136,16.548186 v 0 c 0.1079544,-0.342637 0.1314228,-0.713438 0.08918,-1.074852 -0.028162,-0.173665 0.028162,-0.333249 0.1314228,-0.474061 0.173666,-0.211215 0.3520252,-0.441205 0.5350785,-0.671195 C 2.7487826,13.854017 2.9224493,13.262614 3.035097,12.680599 3.2228435,11.784107 3.0538726,10.840678 3.1477384,9.9066361 3.283855,9.1603419 3.706286,8.5032276 4.180347,7.9305989 2.0353382,8.2028321 2.2559409,11.502486 2.2371653,13.182822 c 0,0.10795 0,0.211215 0,0.328556 L 0.28458701,13.487899 C 0.36907372,10.507416 0.75864821,7.024709 4.5605247,7.4706078 5.4710978,7.1702123 6.3769776,6.8557369 7.3438739,6.8322679 c 2.252965,0.018776 3.9896241,0.6571143 6.2285071,0.4271243 3.130681,-0.4036553 3.482707,-4.1773711 6.895009,-4.2430821 0.427125,0 0.863637,0.065708 1.271985,0.1877466 0.647728,-0.1455032 1.31423,-0.1830533 1.976038,-0.1314221 -0.352026,0.2769265 -0.680582,0.5491598 -1.00914,0.8307796 -0.136117,0.8683298 -0.03286,1.7460474 -0.06571,2.6190716 0.0094,0.2816199 -0.06571,1.5864618 -0.131423,2.1684771 0.03755,0.5069178 -0.511611,0.7650699 -1.032608,0.7134387 C 21.213687,9.3762444 21.194913,9.1884928 20.824112,9.1415562 20.584734,8.9913584 20.673914,8.7003508 20.650446,8.460973 20.608204,7.8789577 20.326582,7.3532655 20.199853,6.7853313 19.993332,6.7055379 19.805584,6.6116658 19.627224,6.513098 c -0.403655,0.6336466 -0.614871,1.3611664 -0.624259,2.1027661 -0.01408,0.6571154 -0.02347,1.3142297 -0.02347,1.9713439 0,-0.04693 0,-0.01408 -0.0094,0.0094 -0.03286,0.502223 -0.173665,0.985672 -0.40835,1.417489 0.863637,0.145503 1.689725,0.40835 2.478261,0.788538 0.31917,0.17836 0.718133,0.337944 0.929349,0.657114 0.0094,0 0.0094,0 0.0094,0.0094 0.112654,0.178359 0.122036,0.384881 0.07979,0.582016 -0.239377,1.103013 -0.535079,2.112154 -0.938734,2.971096 z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the plot development icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The plot development icon.
 */
export function PlotDevelopmentIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M9,19L5,19L5,15L7,15L7,17L9,17L9,19ZM13,15L13,9L12,9L10,10L10,11L11.5,11L11.5,15L13,15ZM17,5L17,2L21,6L17,10L17,7L16,7L16,5L17,5ZM17,17L17,13L19,13L19,19L15,19L15,17L17,17ZM13,19L11,19L11,17L13,17L13,19ZM11,7L11,5L14,5L14,7L11,7ZM7,7L7,13L5,13L5,5L9,5L9,7L7,7Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the official development icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The official development icon.
 */
export function OfficialDevelopmentIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M7,17L17,17L17,11L19,11L19,19L5,19L5,5L17,5L17,2L21,6L17,10L17,7L7,7L7,17ZM12,9L13.5,9L13.5,12L15,12L15,13.5L13.5,13.5L13.5,15L12,15L12,13.5L9,13.5L9,11L10.5,10L10.5,12L12,12L12,11L10.5,11L10.5,10L12,9Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the industrial estate icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The industrial estate icon.
 */
export function IndustrialEstateIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M12,3L20,9L20,21L4,21L4,9L12,3ZM11.5,15L6.5,15L6.5,19L11.5,19L11.5,15ZM17.5,15L12.5,15L12.5,19L17.5,19L17.5,15ZM14.5,10L9.5,10L9.5,14L14.5,14L14.5,10Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the property shell icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The property shell icon.
 */
export function PropertyShellIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="m4 9 8-6 8 6Zm0 12h9v-4H4Zm11 0h5v-4h-5ZM4 15h5v-4H4Zm7 0h9v-4h-9Z" />
      </g>
    </SvgIcon>
  );
}

/**
 * Method to get the support agent icon.
 *
 * @param {object} props The passed in properties.
 * @returns {JSX.Element} The property shell icon.
 */
export function SupportAgentIcon(props) {
  return (
    <SvgIcon {...props}>
      <g>
        <path d="M21,12.22C21,6.73,16.74,3,12,3c-4.69,0-9,3.65-9,9.28C2.4,12.62,2,13.26,2,14v2c0,1.1,0.9,2,2,2h1v-6.1 c0-3.87,3.13-7,7-7s7,3.13,7,7V19h-8v2h8c1.1,0,2-0.9,2-2v-1.22c0.59-0.31,1-0.92,1-1.64v-2.3C22,13.14,21.59,12.53,21,12.22z" />
        <circle cx="9" cy="13" r="1" />
        <circle cx="15" cy="13" r="1" />
        <path d="M18,11.03C17.52,8.18,15.04,6,12.05,6c-3.03,0-6.29,2.51-6.03,6.45c2.47-1.01,4.33-3.21,4.86-5.89 C12.19,9.19,14.88,11,18,11.03z" />
      </g>
    </SvgIcon>
  );
}
